import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { Container, SectionHeading, SectionSubheading } from "./shared";

type TestimonialProps = {
  quote: string;
  author: string;
  role: string;
  avatar: string;
  rating: number;
  delay?: number;
};

function TestimonialCard({ quote, author, role, avatar, rating, delay = 0 }: TestimonialProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-6 md:p-8 h-full flex flex-col"
    >
      {/* Quote icon */}
      <div className="mb-4">
        <Quote className="w-8 h-8 text-emerald-500/30" />
      </div>

      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < rating ? 'text-amber-400 fill-amber-400' : 'text-neutral-300'
            }`}
          />
        ))}
      </div>

      {/* Quote */}
      <p className="text-foreground mb-6 flex-grow">
        "{quote}"
      </p>

      {/* Author */}
      <div className="flex items-center gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <div className="w-12 h-12 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-lg font-semibold text-muted-foreground">
          {avatar}
        </div>
        <div>
          <div className="font-semibold text-foreground">{author}</div>
          <div className="text-sm text-muted-foreground">{role}</div>
        </div>
      </div>
    </motion.div>
  );
}

const testimonials = [
  {
    quote: "Lint has completely changed how I run my online courses. The built-in community feature are exactly what I needed. I hated juggling multiple tools.",
    author: "Sienna Morgan",
    role: "Design Educator",
    avatar: "SM",
    rating: 5,
  },
  {
    quote: "After trying several platforms, Lint stands out for its simplicity and pricing. The video player with chapters is a game-changer for my students. They love being able to jump to specific topics.",
    author: "Jordan Cho",
    role: "Tech Instructor",
    avatar: "JC",
    rating: 5,
  },
  {
    quote: "The community feature is incredible. My students actually engage with each other now, and I can easily answer questions without leaving the platform. It's saved me hours every week.",
    author: "Elena Ruiz",
    role: "Business Coach",
    avatar: "ER",
    rating: 5,
  },
];

export function Testimonials() {
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-neutral-50 dark:bg-neutral-950/50">
      <Container>
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading className="mb-4">
              Loved by Creators
            </SectionHeading>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <SectionSubheading className="mx-auto">
              See what creators are saying about their experience with Lint.
            </SectionSubheading>
          </motion.div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <TestimonialCard
              key={i}
              {...testimonial}
              delay={i * 0.1}
            />
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex flex-wrap items-center justify-center gap-8 md:gap-16 mt-12 pt-12 border-t border-neutral-200 dark:border-neutral-800"
        >
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-foreground">4.9/5</div>
            <div className="text-sm text-muted-foreground mt-1">Beta Rating</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-foreground">18</div>
            <div className="text-sm text-muted-foreground mt-1">Active Creators</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-foreground">180+</div>
            <div className="text-sm text-muted-foreground mt-1">Students Enrolled</div>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}

